/**
 * The guides collection: the only collection that publishes anything.
 *
 * `buildCollection<GuideDocument>` is load-bearing rather than decorative. The
 * generic makes `properties` a mapped type over the content model, so a field
 * renamed in `packages/schema/src/types.ts` fails this file at compile time
 * instead of silently disappearing from the editor.
 *
 * The form is arranged around one idea: an editor should only be asked for
 * things a computer cannot work out. Everything a computer can work out — the
 * slug, the canonical URL, the reading time, the primary question, the excerpt,
 * the SEO title and description, the keywords, an image's dimensions, every
 * timestamp, and who published it — is derived in `src/derive/`, written into an
 * ordinary editable field, and shown back under the title so it is never a
 * surprise. Nothing here lowers the publish gate; the gate still decides.
 *
 * What is left for a human: the title, the direct answer, the body, the
 * category, the author, and the status. The direct answer is the deliberate
 * one. It is rendered under the H1 and published as the article's abstract, and
 * Zennopay is a registered MSB, so a machine-written version of it would be an
 * invented factual claim shipped under a licensed brand. `DirectAnswerField`
 * makes writing it easy instead of writing it for you.
 */

import { AnyProperty, buildCollection, EntityCollection } from "@firecms/core";
import type { CtaVariant, Guide, GuideStatus, ResourceType } from "@zennopay/schema";

import {
    fillEmptyDerivable,
    REVIEW_INTERVAL_MONTHS,
    withPublishDefaults
} from "../derive/autofill";
import { DEFAULT_SITE_ORIGIN } from "../derive/text";
import { EXCERPT_MAX } from "../derive/text";
import {
    DerivedNumberField,
    DerivedTextField,
    PrimaryKeywordField,
    SecondaryKeywordsField
} from "../fields/DerivedFields";
import { AuthorRefField } from "../fields/AuthorRefField";
import { DirectAnswerField } from "../fields/DirectAnswerField";
import { GuideTitleField } from "../fields/GuideTitleField";
import { PropertyGroupField, PropertyGroupProps } from "../fields/PropertyGroupField";
import { ScheduledPublishField } from "../fields/ScheduledPublishField";
import { PublishGateView } from "../gate/PublishGateView";
import { PublishStateField, PublishStatePreview } from "../publish/PublishStateField";
import {
    allowedStatuses,
    canEdit,
    canPublish,
    canReview,
    emailOf,
    roleOf,
    STATUS_LABELS
} from "../roles";
import { idRef, idRefArray, isoDate, mediaRef, stringArray } from "./properties";

/**
 * The document body, which is the model minus its id.
 *
 * The exporter treats the Firestore document id as the record's identity and
 * overwrites any stored `id` with it (`packages/export/src/snapshot.ts`), so
 * duplicating it as an editable field would create a second source of truth
 * that is authoritative nowhere.
 */
export type GuideDocument = Omit<Guide, "id">;

const SITE_ORIGIN = import.meta.env.VITE_SITE_ORIGIN || DEFAULT_SITE_ORIGIN;

const RESOURCE_TYPES: Record<ResourceType, string> = {
    "guide": "Guide",
    "report": "Report",
    "industry-guide": "Industry guide",
    "market-page": "Market page",
    "tool": "Tool",
    "policy": "Policy",
    "docs": "Docs"
};

const CTA_VARIANTS: Record<CtaVariant, string> = {
    "talk-to-us": "Talk to us",
    "view-docs": "View docs",
    "explore-coverage": "Explore coverage",
    "corridor-assessment": "Corridor assessment"
};

/**
 * A property moved into a collapsible group, and so out of the flat form.
 *
 * `disabled.hidden` is FireCMS's only way to keep a property out of the form
 * while leaving it in the schema, which is exactly what a grouped field needs:
 * still validated, still saved, just drawn somewhere else. `PropertyGroupField`
 * strips the flag back off before rendering it, or the input would arrive
 * disabled.
 */
const grouped = <T extends AnyProperty>(property: T): T =>
    ({ ...property, disabled: { hidden: true } }) as T;

/**
 * The panel that renders `anchor` together with the properties it groups.
 *
 * The anchor's own `Field` is carried across rather than dropped: grouping a
 * property is a layout decision, and a hero image that loses its uploader on
 * the way into a panel is back to asking an editor for a width in pixels.
 */
const group = <T extends AnyProperty>(options: PropertyGroupProps & { anchor: T }): T => {
    const anchor = options.anchor as AnyProperty & { customProps?: unknown };
    return {
        ...options.anchor,
        Field: PropertyGroupField,
        customProps: {
            ...options,
            selfField: options.selfField ?? anchor.Field,
            selfProps: options.selfProps ?? anchor.customProps
        }
    } as T;
};

/* ---------------------------------------------------------- group anchors -- */

const readingTime = {
    dataType: "number" as const,
    name: "Reading time",
    description: "Minutes. Counted from the body, and recounted as the body changes.",
    validation: { required: true, min: 1 },
    hideFromCollection: true
};

const seoTitle = {
    dataType: "string" as const,
    name: "SEO title",
    description: "The <title> tag. Truncated in results past about 60 characters.",
    validation: { required: true },
    hideFromCollection: true
};

const heroImage = { ...mediaRef("Hero image", "Shown at the top of the guide."), hideFromCollection: true };

const sourceIds = {
    ...idRefArray("Sources", "sources", {
        description: "Everything this guide cites.",
        previewProperties: ["title", "publisher"],
        hideFromCollection: true
    })
};

const relatedGuideIds = {
    ...idRefArray("Related guides", "guides", {
        previewProperties: ["title"],
        hideFromCollection: true
    })
};

export const guidesCollection: EntityCollection<GuideDocument> = buildCollection<GuideDocument>({
    id: "guides",
    path: "guides",
    name: "Guides",
    singularName: "Guide",
    icon: "Article",
    group: "Content",
    description: "Everything published to zennopay.com/guides/",
    textSearchEnabled: true,
    defaultSize: "s",

    /* Mirrors `match /guides/{guideId}` in firestore.rules. A reviewer gets
       `edit` here because they must be able to move a guide forward, even though
       the rules will only accept a status change from them: the alternative is a
       reviewer who can open a guide and press nothing. */
    permissions: ({ authController }) => {
        const role = roleOf(authController);
        return {
            read: role !== undefined,
            create: canEdit(role),
            edit: canEdit(role) || canReview(role) || canPublish(role),
            delete: role === "admin"
        };
    },

    entityViews: [
        {
            key: "publish_gate",
            name: "Publish gate",
            Builder: PublishGateView
        }
    ],

    /* What an editor has to write, then what they have to decide, then the
       groups. Everything below `relatedGuideIds` is either derived, optional,
       or bookkeeping, and none of it stands between a blank form and a draft. */
    propertiesOrder: [
        "publishState",
        "title", "primaryQuestion", "directAnswer", "excerpt", "body",
        "categoryId", "authorId",
        "status", "publishedAt", "scheduledPublishAt", "slug",
        "readingTime", "seoTitle", "heroImage", "sourceIds", "relatedGuideIds",
        "createdAt", "updatedAt", "importedAt", "publishedBy", "gateExemption"
    ],

    properties: {

        /* First on the form, above the title, and that position is the whole
           point. The publish function writes this record on every attempt and
           nothing rendered it, so a `blocked` or `stale` publish was visible
           only to somebody who thought to open the Firestore console — which
           is to say, never to the editor it concerned.

           `disabled` and not `readOnly`: FireCMS swaps a read-only property's
           component for its own renderer *before* it looks at `Field`, so
           `readOnly: true` would quietly discard `PublishStateField` and print
           the raw map. `disabled` refuses input and keeps the component. */
        publishState: {
            dataType: "map",
            name: "Publish state",
            description:
                "Written by the publish pipeline, never by hand. It reports what happened to "
                + "the last attempt and, for a stale guide, what readers are being served now.",
            disabled: true,
            /* See `mediaRef`: without an explicit null, FireCMS seeds a new
               guide with an empty shell built from the children below. */
            defaultValue: null,
            expanded: false,
            Field: PublishStateField,
            Preview: PublishStatePreview,
            properties: {
                stage: {
                    dataType: "string",
                    name: "Stage",
                    enumValues: {
                        published: "Published",
                        unpublished: "Unpublished",
                        blocked: "Blocked",
                        stale: "Stale",
                        degraded: "Degraded"
                    }
                },
                message: { dataType: "string", name: "Message" },
                updatedAt: { dataType: "string", name: "Reported at" },
                actor: { dataType: "string", name: "Actor" },
                snapshotLocation: { dataType: "string", name: "Snapshot" },
                revertOf: { dataType: "string", name: "Revert of" },
                issues: {
                    dataType: "array",
                    name: "Issues",
                    of: {
                        dataType: "map",
                        properties: {
                            code: { dataType: "string", name: "Code" },
                            field: { dataType: "string", name: "Field" },
                            message: { dataType: "string", name: "Message" },
                            severity: { dataType: "string", name: "Severity" }
                        }
                    }
                }
            }
        },

        title: {
            dataType: "string",
            name: "Title",
            description: "The slug, canonical URL, SEO title, and keywords all follow this.",
            validation: { required: true, trim: true },
            Field: GuideTitleField
        },

        /* Suggested from the title rather than invented: every word in the
           suggestion is a word the editor typed, and it is offered in an
           ordinary field badged "Auto" rather than filled in behind a panel. */
        primaryQuestion: {
            dataType: "string",
            name: "Primary question",
            description: "The question this guide answers, in the reader's words.",
            validation: { required: true },
            Field: DerivedTextField,
            customProps: {
                derivedKey: "primaryQuestion",
                note: "Suggested from your title. Rewrite it in the words a reader would actually search."
            }
        },
        directAnswer: {
            dataType: "string",
            name: "Direct answer",
            description: "Rendered under the H1, and published as the article abstract.",
            multiline: true,
            validation: { required: true },
            Field: DirectAnswerField
        },
        excerpt: {
            dataType: "string",
            name: "Excerpt",
            description: "Hub card summary, and the meta description is taken from it.",
            multiline: true,
            validation: { required: true },
            Field: DerivedTextField,
            customProps: { derivedKey: "excerpt", multiline: true, softLimit: EXCERPT_MAX }
        },
        body: {
            dataType: "string",
            name: "Body",
            description: "Markdown. Rendered to sanitized HTML at export time, never in the browser.",
            markdown: true,
            validation: { required: true }
        },

        categoryId: idRef("Category", "categories", {
            required: true,
            previewProperties: ["name"]
        }),
        /* Defaulted to the author record linked to the signed-in account, when
           one exists. `AuthorRefField` renders the ordinary picker and adds
           only the default and a line saying where it came from; a team byline,
           which has no account, is picked by hand exactly as before. */
        authorId: {
            ...idRef("Author", "authors", {
                required: true,
                description: "Unattributed guides are not published.",
                previewProperties: ["name", "role"]
            }),
            Field: AuthorRefField
        },

        /* The workflow, and the only place role matters to the shape of the form.
           Offering a status the rules will reject is how an editor learns the
           workflow by being told "permission denied" instead of by being shown
           what is possible. */
        status: ({ authController, propertyValue }) => {
            const role = roleOf(authController);
            const current = (propertyValue as GuideStatus | undefined) ?? "draft";
            const allowed = allowedStatuses(role, current);
            return {
                dataType: "string",
                name: "Status",
                defaultValue: "draft",
                validation: { required: true },
                enumValues: Object.fromEntries(
                    allowed.map((status) => [status, STATUS_LABELS[status]])
                ),
                description: allowed.length === 1
                    ? "Your role cannot move this guide out of its current status."
                    : "Draft to review to approved to published. No step can be skipped."
            };
        },

        publishedAt: isoDate("Published at", {
            description: "Set to the moment of publication if you leave it empty."
        }),

        /* The control `functions/src/scheduledPublish.ts` has been waiting for.
           An ISO string rather than a Timestamp, because the scheduler's query
           orders by type before value and the gate parses a string. */
        scheduledPublishAt: {
            ...isoDate("Publish automatically at", {
                description:
                    "Optional. An approved guide is promoted within five minutes of this "
                    + "time — if it passes the publish gate then. Scheduling is not a promise "
                    + "to publish."
            }),
            hideFromCollection: true,
            Field: ScheduledPublishField
        },

        slug: {
            dataType: "string",
            name: "Slug",
            description: "The URL segment. Changing a published slug needs a 301 redirect.",
            validation: {
                required: true,
                trim: true,
                matches: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                matchesMessage: "Lowercase words separated by single hyphens."
            },
            Field: DerivedTextField,
            customProps: { derivedKey: "slug" }
        },

        /* ---- group: article details, anchored on reading time ---- */

        readingTime: group({
            anchor: readingTime,
            title: "Article details",
            description: "Reading time, resource type, call to action, FAQ, review dates.",
            members: [
                "resourceType", "ctaVariant", "featured", "sortOrder",
                "faq", "lastReviewedAt", "nextReviewAt"
            ],
            selfField: DerivedNumberField,
            selfProps: { derivedKey: "readingTime" }
        }),
        resourceType: grouped({
            dataType: "string",
            name: "Resource type",
            defaultValue: "guide",
            validation: { required: true },
            enumValues: RESOURCE_TYPES,
            hideFromCollection: true
        }),
        ctaVariant: grouped({
            dataType: "string",
            name: "Call to action",
            defaultValue: "talk-to-us",
            validation: { required: true },
            enumValues: CTA_VARIANTS,
            hideFromCollection: true
        }),
        featured: grouped({
            dataType: "boolean",
            name: "Featured",
            description: "Featured on the hub. Only one guide should carry this.",
            defaultValue: false,
            hideFromCollection: true
        }),
        /* Not marked required in the form, and `onPreSave` guarantees a number
           instead. FireCMS's `getDefaultValueFor` tests `defaultValue ||
           defaultValue === null`, so a default of `0` is discarded as falsy and
           the field opens empty; a required field nobody can leave alone is
           exactly the manual entry this form is trying to remove. The saved
           document always carries a number, so nothing downstream changes. */
        sortOrder: grouped({
            dataType: "number",
            name: "Sort order",
            description: "Ties are broken by publish date, so leaving this at 0 is a fine answer.",
            hideFromCollection: true
        }),
        faq: grouped({
            dataType: "array",
            name: "FAQ",
            description: "Rendered visibly on the page, and as FAQPage JSON-LD.",
            hideFromCollection: true,
            defaultValue: [],
            of: {
                dataType: "map",
                properties: {
                    question: {
                        dataType: "string",
                        name: "Question",
                        validation: { required: true }
                    },
                    answer: {
                        dataType: "string",
                        name: "Answer",
                        multiline: true,
                        validation: { required: true }
                    }
                }
            }
        }),
        lastReviewedAt: grouped(isoDate("Last reviewed at", { hideFromCollection: true })),
        nextReviewAt: grouped(isoDate("Next review due", {
            description: `Set to ${REVIEW_INTERVAL_MONTHS} months after publication if left empty. Drives the overdue-review queue.`,
            hideFromCollection: true
        })),

        /* ---- group: search metadata, anchored on the SEO title ---- */

        seoTitle: group({
            anchor: seoTitle,
            title: "Search metadata — filled in for you",
            description: "Derived from the title, excerpt, and body. Read it before publishing.",
            members: [
                "metaDescription", "canonicalUrl",
                "primaryKeyword", "secondaryKeywords",
                "openGraphTitle", "openGraphDescription"
            ],
            selfField: DerivedTextField,
            selfProps: { derivedKey: "seoTitle", softLimit: 60 }
        }),
        metaDescription: grouped({
            dataType: "string",
            name: "Meta description",
            description: "Truncated past about 160 characters.",
            multiline: true,
            validation: { required: true },
            hideFromCollection: true,
            Field: DerivedTextField,
            customProps: { derivedKey: "metaDescription", multiline: true, softLimit: 160 }
        }),
        canonicalUrl: grouped({
            dataType: "string",
            name: "Canonical URL",
            url: true,
            validation: { required: true },
            hideFromCollection: true,
            Field: DerivedTextField,
            customProps: { derivedKey: "canonicalUrl" }
        }),
        primaryKeyword: grouped({
            dataType: "string",
            name: "Primary keyword",
            validation: { required: true },
            hideFromCollection: true,
            Field: PrimaryKeywordField,
            customProps: { derivedKey: "primaryKeyword" }
        }),
        secondaryKeywords: grouped({
            ...stringArray("Secondary keywords"),
            defaultValue: [],
            hideFromCollection: true,
            Field: SecondaryKeywordsField,
            customProps: { derivedKey: "secondaryKeywords" }
        }),
        openGraphTitle: grouped({
            dataType: "string",
            name: "Open Graph title",
            description: "Only if the social card should read differently from the SEO title.",
            hideFromCollection: true
        }),
        openGraphDescription: grouped({
            dataType: "string",
            name: "Open Graph description",
            multiline: true,
            hideFromCollection: true
        }),

        /* ---- group: images ---- */

        heroImage: group({
            anchor: heroImage,
            title: "Images",
            description: "Dimensions are read from the file and oversized uploads are downscaled.",
            members: ["openGraphImage"]
        }),
        openGraphImage: grouped({
            ...mediaRef("Open Graph image", "Social share image."),
            hideFromCollection: true
        }),

        /* ---- group: evidence and review ---- */

        sourceIds: group({
            anchor: sourceIds,
            title: "Evidence and review",
            description: "What this guide cites, and who signed the risky parts off.",
            members: ["claimIds", "reviewerId"]
        }),
        claimIds: grouped(idRefArray("Claims", "claims", {
            description: "A claim whose source is not attached above blocks publishing.",
            previewProperties: ["claimText", "riskLevel"],
            hideFromCollection: true
        })),
        reviewerId: grouped(idRef("Reviewer", "reviewers", {
            description: "Required for compliance content and for any compliance-risk claim.",
            previewProperties: ["name", "role"],
            hideFromCollection: true
        })),

        /* ---- group: related content ---- */

        relatedGuideIds: group({
            anchor: relatedGuideIds,
            title: "Related content",
            description: "Cross-links rendered at the foot of the guide.",
            members: ["relatedRailIds", "relatedCountryIds"]
        }),
        relatedRailIds: grouped(idRefArray("Related rails", "paymentRails", {
            previewProperties: ["name"],
            hideFromCollection: true
        })),
        relatedCountryIds: grouped(idRefArray("Related countries", "countries", {
            previewProperties: ["name"],
            hideFromCollection: true
        })),

        /* ---- bookkeeping ---- */

        /* Never an input. Taken from the signed-in account at the moment the
           status crosses into published; see `withPublishDefaults`. Shown at
           all only so the person named can see that they are named. */
        publishedBy: {
            dataType: "string",
            name: "Published by",
            email: true,
            readOnly: true,
            hideFromCollection: true,
            description:
                "Recorded automatically from whoever is signed in when this guide is published. "
                + "It is the actor on the audit event, so it is not something anyone types."
        },
        createdAt: {
            ...isoDate("Created at", { hideFromCollection: true }),
            readOnly: true
        },
        updatedAt: {
            ...isoDate("Updated at", { hideFromCollection: true }),
            readOnly: true
        },
        importedAt: {
            ...isoDate("Imported at", {
                description: "Set by the importer on content migrated from the static generator.",
                hideFromCollection: true
            }),
            readOnly: true
        },

        /* Grandfathered debt, not a general escape hatch: the waiver names the
           exact issue codes it covers, so a problem introduced by a later edit
           still blocks. Only an admin may widen it. */
        gateExemption: ({ authController }) => ({
            dataType: "map",
            name: "Migration exemption",
            description:
                "Only for guides that were already public before the CMS existed. " +
                "Waived issues stay visible and keep the guide in the review queue.",
            expanded: false,
            /* See `mediaRef`: without an explicit null a new guide is seeded
               with an empty exemption whose required fields immediately fail,
               and the only feedback is "a property of this map has an error"
               on a panel nobody opened. */
            defaultValue: null,
            hideFromCollection: true,
            readOnly: roleOf(authController) !== "admin",
            properties: {
                reason: {
                    dataType: "string",
                    name: "Reason",
                    enumValues: { migration: "Pre-CMS migration" },
                    validation: { required: true }
                },
                grantedBy: {
                    dataType: "string",
                    name: "Granted by",
                    email: true,
                    validation: { required: true }
                },
                grantedAt: isoDate("Granted at", { required: true }),
                waivedCodes: stringArray(
                    "Waived issue codes",
                    "Exact gate issue codes recorded at import, e.g. sources.required."
                )
            }
        })
    },

    callbacks: {
        /**
         * The last chance to fill in what nobody should have to type.
         *
         * The form derives everything live, but a field inside a collapsed panel
         * is never mounted and therefore never runs its own derivation, and a
         * copied entity never opens a form at all. This is the backstop, and it
         * only ever writes into a gap: anything an editor put there is left
         * exactly as it is.
         *
         * Reviewers are excluded on purpose. `firestore.rules` restricts their
         * diff to status, reviewerId, lastReviewedAt and updatedAt, so quietly
         * adding a derived keyword to their save would get the whole approval
         * refused with a permission error that explains nothing.
         */
        onPreSave: ({ values, previousValues, context }) => {
            const now = new Date().toISOString();
            const role = roleOf(context.authController);

            const becamePublished =
                values.status === "published" && previousValues?.status !== "published";
            const becameApproved =
                values.status === "approved" && previousValues?.status !== "approved";

            /* Timestamps are bookkeeping, not editorial content, so the form
               does not ask for them. `createdAt` is only written when absent,
               which keeps an imported guide's original date rather than
               resetting it on first edit. */
            let saved: Partial<GuideDocument> = {
                ...values,
                createdAt: values.createdAt ?? now,
                updatedAt: now,
                ...(becameApproved && !values.lastReviewedAt ? { lastReviewedAt: now } : {})
            };

            /* The same function the publish gate panel previews with, so the
               panel cannot promise something the save then fails to do. The
               publisher's identity is the one thing the panel cannot preview,
               because it is only true of the session that presses save. */
            if (becamePublished) {
                saved = withPublishDefaults(saved, now, emailOf(context.authController));
            }

            if (!canEdit(role)) return saved;

            const derived = fillEmptyDerivable(saved, SITE_ORIGIN);
            return {
                ...derived,

                /* The content model types these as arrays. An absent array
                   reaches the exporter as `undefined` and every consumer then
                   has to defend against it. */
                faq: derived.faq ?? [],
                sourceIds: derived.sourceIds ?? [],
                claimIds: derived.claimIds ?? [],
                relatedGuideIds: derived.relatedGuideIds ?? [],
                relatedRailIds: derived.relatedRailIds ?? [],
                relatedCountryIds: derived.relatedCountryIds ?? [],
                secondaryKeywords: derived.secondaryKeywords ?? [],

                /* Every guide needs a position on the hub, and 0 is the honest
                   one: the exporter breaks ties by publish date. */
                sortOrder: derived.sortOrder ?? 0
            };
        }
    }
});
